import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { server } from "../server";

const AdminSellerTransactions = () => {
  const { sellerId } = useParams();
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [sellerName, setSellerName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [amount, setAmount] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [transactionType, setTransactionType] = useState(''); // 'Credit' or 'Debit'
  const [transactionReason, setTransactionReason] = useState(''); // Reason
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // Load saved date filters only if not a page reload
  useEffect(() => {
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const isPageReload = navigationEntry && navigationEntry.type === 'reload';
    
    if (!isPageReload) {
      const savedStartDate = sessionStorage.getItem(`sellerTransactions_${sellerId}_startDate`);
      const savedEndDate = sessionStorage.getItem(`sellerTransactions_${sellerId}_endDate`);
      
      if (savedStartDate) setStartDate(savedStartDate);
      if (savedEndDate) setEndDate(savedEndDate);
    } else {
      // Clear filters on page reload
      sessionStorage.removeItem(`sellerTransactions_${sellerId}_startDate`);
      sessionStorage.removeItem(`sellerTransactions_${sellerId}_endDate`);
    }
  }, [sellerId]);

  // Fetch Transactions Function
  const fetchTransactions = useCallback(async () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        alert("Start date cannot be later than end date.");
        return;
      }
    }

    try {
      const { data } = await axios.get(`${server}/sellerTransaction/admin/get-transactions/${sellerId}`, {
        params: { startDate, endDate },
        withCredentials: true,
      });

      if (data.success) {
        const sortedTransactions = data.transactions
          .map(transaction => ({
            id: transaction._id,
            date: new Date(transaction.transactionDate),
            formattedDate: new Date(transaction.transactionDate).toLocaleDateString(),
            orderId: transaction.orderId || transaction._id, // Ensure orderId is set
            credit: transaction.type === "Credit" ? transaction.amount : 0,
            debit: transaction.type === "Debit" ? transaction.amount : 0,
            type: transaction.type, // 'Credit' or 'Debit'
            transactionType: transaction.transactionType, // e.g., 'Payment recorded', 'Order Received'
            referenceNumber: transaction.referenceNumber || "N/A",
          }))
          .sort((a, b) => b.date - a.date);

        setTransactions(sortedTransactions);
        setBalance(data.balance.toFixed(2));
      } else {
        console.error("Failed to fetch transactions:", data.message);
      }
    } catch (error) {
      console.error("Error fetching transactions", error.response ? error.response.data : error.message);
    }
  }, [sellerId, startDate, endDate]);

  // Fetch Transactions on Component Mount and When Dependencies Change
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Fetch seller/shop info to display seller name instead of id
  useEffect(() => {
    const fetchSellerName = async () => {
      try {
        const { data } = await axios.get(`${server}/shop/get-shop-info/${sellerId}`, { withCredentials: true });
        if (data && data.success && data.shop) {
          setSellerName(data.shop.name || '');
        }
      } catch (error) {
        console.error('Error fetching seller info', error.response ? error.response.data : error.message);
      }
    };

    if (sellerId) fetchSellerName();
  }, [sellerId]);

  // Open Create Transaction Popup
  const openCreateTransactionPopup = (type, defaultReason) => {
    setTransactionType(type); // 'Credit' or 'Debit'
    setTransactionReason(defaultReason); // Pre-fill reason if needed
    setAmount('');
    setTransactionDate('');
    setReferenceNumber('');
    setShowCreatePopup(true);
  };

  // Handle Create Transaction
  const handleCreateTransaction = async () => {
    // Basic Validation
    if (!amount || !transactionDate) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      const transactionData = {
        amount,
        transactionDate,
        referenceNumber,
        transactionType: transactionReason || (transactionType === 'Credit' ? 'Payment recorded' : ''),
        type: transactionType, // 'Credit' or 'Debit'
      };
  
      const { data } = await axios.post(`${server}/sellerTransaction/create-transaction/${sellerId}`, transactionData, {
        withCredentials: true,
      });
  
      if (data.success) {
        const newTransaction = {
          id: data.transaction._id,
          date: new Date(data.transaction.transactionDate),
          formattedDate: new Date(data.transaction.transactionDate).toLocaleDateString(),
          orderId: data.transaction.orderId || "N/A",
          credit: data.transaction.type === "Credit" ? data.transaction.amount : 0,
          debit: data.transaction.type === "Debit" ? data.transaction.amount : 0,
          type: data.transaction.type, // 'Credit' or 'Debit'
          transactionType: data.transaction.transactionType,
          referenceNumber: data.transaction.referenceNumber || "N/A",
        };
  
        // Add new transaction at the top
        setTransactions([newTransaction, ...transactions]);

        // Update balance
        const balanceAdjustment = data.transaction.type === "Debit" ? data.transaction.amount : -data.transaction.amount;
        setBalance((prevBalance) => (parseFloat(prevBalance) + balanceAdjustment).toFixed(2));

        setShowCreatePopup(false);
      }
    } catch (error) {
      console.error("Error creating transaction", error.response ? error.response.data : error.message);
      alert("Failed to create transaction. Please try again.");
    }
  };  

  // Open Delete Confirmation Popup
  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteConfirm(true);
  };

  // Confirm Deletion
  const confirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      // Updated delete URL to match backend
      const { data } = await axios.delete(`${server}/sellerTransaction/delete-transaction/${transactionToDelete.id}`, {
        withCredentials: true,
      });

      if (data.success) {
        // Remove transaction from state
        const updatedTransactions = transactions.filter(t => t.id !== transactionToDelete.id);
        setTransactions(updatedTransactions);

        // Update balance
        const balanceAdjustment = transactionToDelete.type === "Debit" ? transactionToDelete.debit : -transactionToDelete.credit;
        setBalance((prevBalance) => (parseFloat(prevBalance) + balanceAdjustment).toFixed(2));

        // Reset deletion states
        setShowDeleteConfirm(false);
        setTransactionToDelete(null);
      } else {
        console.error("Deletion failed:", data.message);
        alert("Failed to delete transaction. Please try again.");
        setShowDeleteConfirm(false);
        setTransactionToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting transaction:", error.response ? error.response.data : error.message);
      alert("An error occurred while deleting the transaction.");
      setShowDeleteConfirm(false);
      setTransactionToDelete(null);
    }
  };

  // Cancel Deletion
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setTransactionToDelete(null);
  };

  return (
    <div className="container" style={{ padding: '20px' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
        Transactions for Seller {sellerName }
      </h2>

      <div style={{ textAlign: 'right', marginBottom: '20px' }}>
        <h3 style={{ color: '#555' }}>Balance: ₹{balance}</h3>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '10px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
          marginBottom: '20px',
          maxWidth: '100%',
          overflowX: 'auto',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          <label style={{ fontSize: '14px', color: '#555' }}>
            Start Date:
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                sessionStorage.setItem(`sellerTransactions_${sellerId}_startDate`, e.target.value);
              }}
              style={{
                marginLeft: '8px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </label>
          <label style={{ fontSize: '14px', color: '#555' }}>
            End Date:
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                sessionStorage.setItem(`sellerTransactions_${sellerId}_endDate`, e.target.value);
              }}
              style={{
                marginLeft: '8px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
              }}
            />
          </label>
          <button
            onClick={fetchTransactions}
            style={{
              padding: '8px 12px',
              backgroundColor: '#4CAF50',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              fontSize: '14px',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#4CAF50')}
          >
            Filter
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button
            onClick={() => openCreateTransactionPopup('Credit', 'Payment recorded')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#007BFF',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              fontSize: '14px',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#007BFF')}
          >
            Create Payment
          </button>
          <button
            onClick={() => openCreateTransactionPopup('Debit', '')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              fontSize: '14px',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#c82333')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#dc3545')}
          >
            Create Debit Transaction
          </button>
          <button
            onClick={() => openCreateTransactionPopup('Credit', '')}
            style={{
              padding: '10px 15px',
              backgroundColor: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              fontSize: '14px',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#218838')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#28a745')}
          >
            Create Credit Transaction
          </button>
        </div>
      </div>

      {/* Create Transaction Popup */}
      {showCreatePopup && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0px 0px 15px rgba(0,0,0,0.2)',
            zIndex: 1000,
            borderRadius: '8px',
            width: '300px',
            maxWidth: '90%',
            textAlign: 'center',
          }}
        >
          <h3 style={{ marginBottom: '20px', fontSize: '18px', color: '#333' }}>
            Create {transactionType === 'Credit' ? 'Credit' : 'Debit'} Transaction
          </h3>
          <label style={{ display: 'block', marginBottom: '15px', fontSize: '14px', color: '#555' }}>
            Amount:
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px',
                marginTop: '5px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '15px', fontSize: '14px', color: '#555' }}>
            Transaction Date:
            <input
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px',
                marginTop: '5px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '15px', fontSize: '14px', color: '#555' }}>
            Reference Number:
            <input
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px',
                marginTop: '5px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '20px', fontSize: '14px', color: '#555' }}>
            Transaction Type / Reason:
            <input
              type="text"
              value={transactionReason}
              onChange={(e) => setTransactionReason(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px',
                marginTop: '5px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            />
          </label>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button
              onClick={handleCreateTransaction}
              style={{
                padding: '10px 15px',
                backgroundColor: '#4CAF50',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#4CAF50')}
            >
              Create Transaction
            </button>
            <button
              onClick={() => setShowCreatePopup(false)}
              style={{
                padding: '10px 15px',
                backgroundColor: '#f0f0f0',
                color: '#333',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e0e0e0')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && transactionToDelete && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0px 0px 15px rgba(0,0,0,0.2)',
            zIndex: 1000,
            borderRadius: '8px',
            width: '300px',
            maxWidth: '90%',
            textAlign: 'center',
          }}
        >
          <h3 style={{ marginBottom: '20px', fontSize: '18px', color: '#333' }}>
            Confirm Deletion
          </h3>
          <p style={{ fontSize: '14px', color: '#555' }}>
            Are you sure you want to delete this transaction?
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
            <button
              onClick={confirmDeleteTransaction}
              style={{
                padding: '10px 15px',
                backgroundColor: '#ff4d4f',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#d9363e')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#ff4d4f')}
            >
              Delete
            </button>
            <button
              onClick={cancelDelete}
              style={{
                padding: '10px 15px',
                backgroundColor: '#f0f0f0',
                color: '#333',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e0e0e0')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Overlay for Popups */}
      {(showCreatePopup || showDeleteConfirm) && (
        <div
          onClick={() => {
            setShowCreatePopup(false);
            setShowDeleteConfirm(false);
            setTransactionToDelete(null);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 999,
          }}
        ></div>
      )}

      {/* Transactions Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f2f2f2' }}>
              <th>Date</th>
              <th>Transaction Type</th>
              <th>Reference Number / Order ID</th>
              <th>Credit</th>
              <th>Debit</th>
              <th style={{ textAlign: 'center' }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td>{transaction.formattedDate}</td>
                <td>{transaction.transactionType}</td>
                <td>
                  {transaction.transactionType === "Payment recorded" ? (
                    transaction.referenceNumber || "N/A"
                  ) : (transaction.transactionType === "Order received" || transaction.transactionType === "Order cancelled") ? (
                    transaction.orderId !== "N/A" ? (
                      <Link to={`/admin/order/${transaction.orderId}`}>{transaction.orderId}</Link>
                    ) : "N/A"
                  ) : (
                    transaction.referenceNumber || "N/A"
                  )}
                </td>
                <td>{transaction.credit > 0 ? `₹${transaction.credit}` : ''}</td>
                <td>{transaction.debit > 0 ? `₹${transaction.debit}` : ''}</td>
                <td style={{ textAlign: 'center' }}>
  {['Credit', 'Debit'].includes(transaction.type) && 
   !['Order Received', 'Order Cancelled'].includes(transaction.transactionType) && (
    <button
      style={{
        backgroundColor: '#5c5a5a',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        padding: '5px 10px',
        cursor: 'pointer',
      }}
      onClick={() => handleDeleteClick(transaction)}
      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#424242')}
      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#5f5d5d')}
    >
      Delete
    </button>
  )}
</td>


              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSellerTransactions;
